import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, AlertTriangle, Calendar, Shield, X, CheckCircle } from 'lucide-react';
import { api, isInLocalMode } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';
import { SpinnerRing } from '@/components/ui/AsyncGuard';

interface Notification {
  id: string;
  type: 'deadline' | 'compliance' | 'expense';
  title: string;
  detail: string;
  urgency: 'high' | 'medium' | 'low';
  timestamp?: string;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('nexus_dismissed_notifs');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeEntity = useAppStore(s => s.activeEntity);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch notifications from compliance calendar + status
  const fetchNotifications = useCallback(async () => {
    if (isInLocalMode() || !activeEntity) return;
    setLoading(true);
    const notifs: Notification[] = [];

    try {
      // Compliance calendar — upcoming deadlines.
      // Phase C Cycle 2 FIX (2026-05-23) — Agent 3 P1 confirmed by Cycle 1
      // Agent 3: pre-fix the consumer read `data.deadlines[].{due_date, days_until}`
      // (snake_case) but the API returns `{events: [{dueDate, daysUntilDue, status}]}`
      // (camelCase). Caught silently by the outer try/catch — bell would always
      // be empty. Phase C amplifies impact: multi-state PTET tenants now have
      // 30+ events that should surface. Post-fix shape matches the API contract
      // verified at compliance-agent/index.js:508 `return { events: allEvents, year }`.
      const calendar = await api.getComplianceCalendar() as { events?: Array<{ id: string; title: string; dueDate: string; daysUntilDue: number; status: string }> };
      if (calendar?.events) {
        for (const d of calendar.events) {
          if (d.daysUntilDue <= 30 && d.status !== 'completed' && d.daysUntilDue >= 0) {
            notifs.push({
              id: `deadline-${d.id || d.title}`,
              type: 'deadline',
              title: d.title,
              detail: `Due ${d.dueDate} (${d.daysUntilDue} days)`,
              urgency: d.daysUntilDue <= 7 ? 'high' : d.daysUntilDue <= 14 ? 'medium' : 'low',
            });
          }
        }
      }
    } catch { /* API may not be available */ }

    try {
      // PP1 (2026-06-01) — enrich the quarterly-estimate deadline with the
      // dollar amount. The compliance calendar shows "Q2 estimated tax payment
      // (TY2026)" with the date but no $; the canonical /tax/estimated-payments
      // endpoint already computes the per-quarter amount due. Match the upcoming
      // quarter to its deadline notif and prepend the amount. Role-gated (viewer+)
      // + non-fatal: if it errors the deadline still shows, just without the $.
      const est = await api.getEstimatedPayments() as {
        quarters?: Array<{ quarter: string; remaining: number; status: string; daysUntilDue: number }>;
      };
      const dueQ = (est?.quarters || []).find(
        q => Number(q.remaining) > 0 && q.status !== 'paid' && q.daysUntilDue >= 0 && q.daysUntilDue <= 30,
      );
      if (dueQ) {
        const target = notifs.find(
          n => n.type === 'deadline' &&
            /estimated tax/i.test(n.title) &&
            new RegExp(`\\b${dueQ.quarter}\\b`).test(n.title),
        );
        if (target) {
          const amt = '$' + Math.round(Number(dueQ.remaining)).toLocaleString('en-US');
          target.detail = `${amt} due — ${target.detail}`;
          if (dueQ.daysUntilDue <= 14) target.urgency = 'high';
        }
      }
    } catch { /* estimate unavailable or role-gated — leave the deadline as-is */ }

    try {
      // Compliance status — flagged items
      const status = await api.getComplianceStatus() as { flaggedCount?: number; pendingCount?: number; complianceScore?: number };
      if (status?.flaggedCount && status.flaggedCount > 0) {
        notifs.push({
          id: 'compliance-flagged',
          type: 'compliance',
          title: `${status.flaggedCount} flagged expense${status.flaggedCount > 1 ? 's' : ''}`,
          detail: 'Review required for compliance',
          urgency: 'high',
        });
      }
      if (status?.complianceScore !== undefined && status.complianceScore < 80) {
        notifs.push({
          id: 'compliance-score',
          type: 'compliance',
          title: `Compliance score: ${status.complianceScore}%`,
          detail: 'Below 80% threshold — review flagged items',
          urgency: status.complianceScore < 60 ? 'high' : 'medium',
        });
      }
    } catch { /* API may not be available */ }

    setNotifications(notifs);
    setLoading(false);
  }, [activeEntity]);

  // Fetch on mount and when entity changes
  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { localStorage.setItem('nexus_dismissed_notifs', JSON.stringify([...next])); } catch { /* */ }
  };

  const active = notifications.filter(n => !dismissed.has(n.id));
  const highCount = active.filter(n => n.urgency === 'high').length;

  const iconForType = (type: string) => {
    switch (type) {
      case 'deadline': return <Calendar className="w-4 h-4 text-amber-500" />;
      case 'compliance': return <Shield className="w-4 h-4 text-red-500" />;
      case 'expense': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default: return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-[var(--radius-md)] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors relative"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-500 dark:text-slate-400" />
        {active.length > 0 && (
          <span className={`absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1 ${highCount > 0 ? 'bg-red-500' : 'bg-amber-500'}`}>
            {active.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-[var(--radius-lg)] shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</p>
            {active.length > 0 && (
              <span className="text-xs text-slate-400">{active.length} active</span>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && active.length === 0 && (
              <div className="px-4 py-8 text-center">
                <SpinnerRing className="w-5 h-5 border-2 mx-auto" />
              </div>
            )}

            {!loading && active.length === 0 && (
              <div className="px-4 py-8 text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">All caught up!</p>
              </div>
            )}

            {active.map(n => (
              <div key={n.id} className={`px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${n.urgency === 'high' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">{iconForType(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{n.detail}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                    className="shrink-0 p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
                    title="Dismiss"
                  >
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
